import { useEffect, useRef, useState } from "react";

/**
 * Subscribe to live-location pushes for a single live-share token.
 * Mirrors `useAlertStatusStream` — same reconnect/backoff shape, just a
 * different room key and message types.
 *
 *   - `live`   → true while the socket is OPEN
 *   - onPosition({lat,lng,accuracy,heading,speed,last_updated}) called on
 *     every "snapshot" (initial) and "position.update" message.
 *   - onEnded() called on "session.ended".
 *
 * Auto-reconnects with exponential backoff (capped at 15s) — if the
 * phone/viewer briefly loses connectivity, this resumes on the SAME
 * token as soon as the socket can reopen; it never creates a new
 * session or a new subscription target.
 */
export function useLiveShareStream(token, { onPosition, onEnded } = {}) {
  const [live, setLive] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const closedRef = useRef(false);
  const posCbRef = useRef(onPosition);
  const endCbRef = useRef(onEnded);

  useEffect(() => { posCbRef.current = onPosition; }, [onPosition]);
  useEffect(() => { endCbRef.current = onEnded; }, [onEnded]);

  useEffect(() => {
    if (!token) return undefined;
    closedRef.current = false;

    const buildWsUrl = () => {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = base.replace(/^http/i, "ws");
      return `${wsBase}/api/ws/live-shares/${encodeURIComponent(token)}`;
    };

    const open = () => {
      if (closedRef.current) return;
      const ws = new WebSocket(buildWsUrl());
      wsRef.current = ws;
      ws.onopen = () => {
        setLive(true);
        retryRef.current = 0;
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg?.type === "snapshot" || msg?.type === "position.update") {
            posCbRef.current?.(msg);
          } else if (msg?.type === "session.ended") {
            endCbRef.current?.();
          }
        } catch (err) {
          console.error("live-share ws message parse failed", err);
        }
      };
      ws.onclose = (ev) => {
        setLive(false);
        wsRef.current = null;
        // 4404 = backend rejected (token not found) → don't retry forever.
        if (closedRef.current || ev?.code === 4404) return;
        const attempt = Math.min(retryRef.current + 1, 5);
        retryRef.current = attempt;
        const delay = Math.min(15000, 1000 * 2 ** (attempt - 1));
        timerRef.current = setTimeout(open, delay);
      };
      ws.onerror = () => {
        // onclose runs immediately after; let it handle the retry.
      };
    };

    open();
    return () => {
      closedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.close(1000, "unmount"); } catch (e) { console.error("ws close failed", e); }
      }
      wsRef.current = null;
    };
  }, [token]);

  return { live };
}
